import { Button, Checkbox, Input, Modal, Switch, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';
import { IEmailNotification, NotificationKey } from 'interfaces';
import { MenuOutlined } from '@ant-design/icons';
import {
  NotificationCategory,
  NOTIFICATION_CATEGORY_LABEL_KEY_MAP,
  NOTIFICATION_LABEL_KEY_MAP,
} from 'helpers/notificationSettings';
import { getTheme } from 'helpers/getTheme';
import api from 'api';
import Spinner from 'components/Spinner/Spinner';
import classes from './NotificationsSettingsModal.module.scss';

type NotificationsSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  mode: 'room' | 'profile';
  roomId?: string;
};

const NotificationsSettingsModal = ({ open, onClose, mode, roomId }: NotificationsSettingsModalProps) => {
  const { t } = useTranslation();
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { available_rooms } = useAppSelector((state) => state.userData);

  const [availableNotifications, setAvailableNotifications] = useState<IEmailNotification[]>([]);
  const [userPermittedNotifications, setUserPermittedNotifications] = useState<NotificationKey[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allRoomsLoading, setAllRoomsLoading] = useState(false);
  const [roomNotificationsMap, setRoomNotificationsMap] = useState<Record<string, NotificationKey[]>>({});
  const [draftActiveKeys, setDraftActiveKeys] = useState<NotificationKey[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory | null>(null);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);
  const [roomSearchQuery, setRoomSearchQuery] = useState<string>('');

  const criticalKeys = useMemo(
    () => availableNotifications.filter((n) => n.priority === 'high').map((n) => n.action_code),
    [availableNotifications]
  );

  const defaultKeys = useMemo(
    () => availableNotifications.filter((n) => n.priority !== 'low').map((n) => n.action_code),
    [availableNotifications]
  );

  const categories = useMemo(() => {
    const grouped = availableNotifications.reduce<Record<string, NotificationKey[]>>((acc, notification) => {
      const category = notification.type;
      acc[category] = acc[category] ? [...acc[category], notification.action_code] : [notification.action_code];
      return acc;
    }, {});

    return (Object.keys(grouped) as NotificationCategory[]).map((category) => ({
      category,
      keys: grouped[category],
    }));
  }, [availableNotifications]);
  
  const filteredRooms = useMemo(() => {
    if (mode !== 'profile') return [];
    
    const query = roomSearchQuery.toLowerCase().trim();
    if (!query) return available_rooms;
    
    return available_rooms.filter((room) => 
      room.name.toLowerCase().includes(query)
    );
  }, [mode, available_rooms, roomSearchQuery]);
  
  const isSettingsChanged = useMemo(() => {
    const current = [...draftActiveKeys].sort().join('|');
    const initial = [...userPermittedNotifications].sort().join('|');
    return current !== initial;
  }, [draftActiveKeys, userPermittedNotifications]);


  useEffect(() => {
    if (!open) return;
    
    const fetchTypes = async () => {
      setTypesLoading(true);
      try {
        const response = await api.getAllEmailNotificationsTypes();
        setAvailableNotifications(response.data.filter(notification => !notification.is_personal));
      } catch (error) {
        messageApi?.error(t('notificationsSettings.errorLoad'));
      } finally {
        setTypesLoading(false);
      }
    };
    
    fetchTypes();
  }, [open, messageApi, t]);

  useEffect(() => {
    if (open) return;
    setSelectedRoomIds([]);
    setDraftActiveKeys([]);
    setUserPermittedNotifications([]);
    setRoomNotificationsMap({});
    setSelectedCategory(null);
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'room' || !roomId) return;

    const fetchRoomSettings = async () => {
      setSettingsLoading(true);
      try {
        const response = await api.getUserEmailNotifications(roomId);
        const permitted = response.data.permitted_notifications;
        setUserPermittedNotifications(permitted);
        setDraftActiveKeys(permitted);
      } catch (error) {
        messageApi?.error(t('notificationsSettings.errorLoad'));
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchRoomSettings();
  }, [open, mode, roomId, messageApi, t]);

  useEffect(() => {
    if (!open || mode !== 'profile' || available_rooms.length === 0) return;

    const fetchAllRoomsNotifications = async () => {
      setAllRoomsLoading(true);
      try {
        const results = await Promise.all(
          available_rooms.map((room) =>
            api.getUserEmailNotifications(room.id).then((res) => ({
              roomId: room.id,
              notifications: res.data.permitted_notifications,
            }))
          )
        );
        const map: Record<string, NotificationKey[]> = {};
        results.forEach(({ roomId: id, notifications }) => {
          map[id] = notifications;
        });
        setRoomNotificationsMap(map);
      } catch (error) {
        messageApi?.error(t('notificationsSettings.errorLoad'));
      } finally {
        setAllRoomsLoading(false);
      }
    };

    fetchAllRoomsNotifications();
  }, [open, mode, available_rooms, messageApi, t]);

  useEffect(() => {
    if (!open || mode !== 'profile' || selectedRoomIds.length === 0) return;

    const loadedIds = selectedRoomIds.filter((id) => id in roomNotificationsMap);
    if (loadedIds.length === 0) return;

    const firstRoomKeys = roomNotificationsMap[loadedIds[0]];
    const keys =
      loadedIds.length === 1
        ? firstRoomKeys
        : loadedIds
            .slice(1)
            .reduce<NotificationKey[]>(
              (acc, id) => acc.filter((key) => roomNotificationsMap[id].includes(key)),
              [...firstRoomKeys]
            );

    setUserPermittedNotifications(keys);
    setDraftActiveKeys(keys);
  }, [open, mode, selectedRoomIds, roomNotificationsMap]);

  const handleToggleKey = (key: NotificationKey) => {
    setDraftActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleToggleCategory = (category: NotificationCategory) => {
    const categoryKeys = categories.find((item) => item.category === category)?.keys || [];
    const isCategoryActive = getIsCategoryActive(category);

    if (isCategoryActive) {
      setDraftActiveKeys((prev) =>
        prev.filter((item) => !categoryKeys.includes(item))
      );
    } else {
      setDraftActiveKeys((prev) => Array.from(new Set([...prev, ...categoryKeys])));
    }
  };

  const handleCategorySelect = (category: NotificationCategory) => {
    setSelectedCategory(category);
  };

  const handleCriticalOnly = () => {
    setDraftActiveKeys(criticalKeys);
  };

  const handleDefault = () => {
    setDraftActiveKeys(defaultKeys);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const roomsToUpdate = mode === 'room' && roomId 
        ? [roomId] 
        : selectedRoomIds;
      
      for (const roomId of roomsToUpdate) {
        await api.updateUserEmailNotificationsSettings(roomId, {
          room_id: roomId,
          actions: draftActiveKeys,
        });
      }
      
      messageApi?.success(t('notificationsSettings.successSave'));
      onClose();
    } catch (error) {
      messageApi?.error(t('notificationsSettings.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (isSettingsChanged) {
      setIsConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const handleConfirmCancel = () => {
    setDraftActiveKeys(userPermittedNotifications);
    setIsConfirmCloseOpen(false);
    onClose();
  };

  const handleConfirmSave = () => {
    setIsConfirmCloseOpen(false);
    handleSave();
  };
  
  const handleRoomSelect = (roomId: string, checked: boolean) => {
    if (checked) {
      setSelectedRoomIds((prev) => [...prev, roomId]);
    } else {
      setSelectedRoomIds((prev) => prev.filter((id) => id !== roomId));
    }
  };
  
  const handleSelectAllRooms = (checked: boolean) => {
    if (checked) {
      setSelectedRoomIds(filteredRooms.map((room) => room.id));
    } else {
      setSelectedRoomIds([]);
    }
  };

  const getIsCategoryActive = (category: NotificationCategory) => {
    const categoryKeys = categories.find((item) => item.category === category)?.keys || [];
    const nonCriticalKeys = categoryKeys.filter((item) => !criticalKeys.includes(item));
    
    if (nonCriticalKeys.length === 0) {
      return categoryKeys.some((key) => draftActiveKeys.includes(key));
    }
    
    return nonCriticalKeys.some((key) => draftActiveKeys.includes(key));
  };

  const isLoading = typesLoading || settingsLoading || allRoomsLoading;
  const isDisabled = mode === 'profile' && selectedRoomIds.length === 0;

  return (
    <>
      <Modal
        open={open}
        onCancel={handleRequestClose}
        className={cn(classes.modal, getTheme())}
        title={t('notificationsSettings.title')}
        footer={null}
        centered
        width={900}
      >
        <div className={classes.content}>
          {mode === 'profile' && (
            <div className={classes.roomsSection}>
              <div className={classes.roomsSectionHeader}>
                <Input.Search
                  placeholder={t('notificationsSettings.searchRooms')}
                  value={roomSearchQuery}
                  onChange={(e) => setRoomSearchQuery(e.target.value)}
                  allowClear
                  className={classes.roomSearch}
                />
              </div>
              
              <div className={classes.roomsList}>
                <div className={classes.roomsListHeader}>
                  <span className={classes.selectedCount}>
                    {t('notificationsSettings.selected', { count: selectedRoomIds.length })}
                  </span>
                  <span className={classes.selectAllButton}>
                  {t('notificationsSettings.selectAll')}
                  <Checkbox
                    checked={selectedRoomIds.length === filteredRooms.length && filteredRooms.length > 0}
                    indeterminate={selectedRoomIds.length > 0 && selectedRoomIds.length < filteredRooms.length}
                    onChange={(e) => handleSelectAllRooms(e.target.checked)}

                  >
                  </Checkbox>
                  </span>
                </div>
                
                <div className={classes.roomsListContent}>
                  {filteredRooms.length === 0 ? (
                    <div className={classes.emptyRooms}>
                      {t('notificationsSettings.noRoomsFound')}
                    </div>
                  ) : (
                    filteredRooms.map((room) => (
                      <label key={room.id} htmlFor={room.id}>

                      <div className={classes.roomItem}>
                        <div className={classes.roomItemLeft}>
                            <span className={classes.roomName}>
                              {room.name}
                            </span>
                        </div>
                        <Checkbox
                          id={room.id}
                          name={room.id}
                          checked={selectedRoomIds.includes(room.id)}
                          onChange={(e) => handleRoomSelect(room.id, e.target.checked)}
                        />
                      </div>
                      </label>

                    ))
                  )}
                </div>
              </div>
              
              {selectedRoomIds.length > 1 && (
                <div className={classes.multiSelectHint}>
                  {t('notificationsSettings.multiSelectHint')}
                </div>
              )}
            </div>
          )}
          
          <div className={classes.notificationsWindow}>
            {settingsLoading ? (
              <div className={classes.loaderContainer}>
                <Spinner size={28} />
              </div>
            ) : selectedCategory ? (
              <>
                <div className={classes.windowHeader}>
                  <span className={classes.windowTitle}>
                    {t(NOTIFICATION_CATEGORY_LABEL_KEY_MAP[selectedCategory])}
                  </span>
                </div>
                <div className={classes.notificationsGrid}>
                  {categories
                    .find((item) => item.category === selectedCategory)
                    ?.keys.map((key) => {
                      const isKeyActive = mode === 'profile' && selectedRoomIds.length === 0
                        ? false
                        : draftActiveKeys.includes(key);

                      return (
                        <div key={key} className={classes.notificationCard}>
                          <span className={classes.notificationLabel}>
                            {t(NOTIFICATION_LABEL_KEY_MAP[key])}
                          </span>
                          <Tooltip 
                            title={isDisabled ? t('notificationsSettings.selectRoomFirst') : ''}
                            placement="top"
                          >
                            <Switch
                              checked={isKeyActive}
                              onChange={() => handleToggleKey(key)}
                              size="small"
                              disabled={isDisabled}
                            />
                          </Tooltip>
                        </div>
                      );
                    })}
                </div>
              </>
            ) : (
              <div className={classes.emptyWindow}>
                {t('notificationsSettings.selectCategory')}
              </div>
            )}
          </div>
          
          <div className={classes.categoriesGrid}>
            {typesLoading ? (
              <div className={classes.loaderContainer}>
                <Spinner size={28} />
              </div>
            ) : (
              categories
                .filter(({ category }) => category !== NotificationCategory.ORGANIZATIONS)
                .map(({ category }) => {
                  const isActive = mode === 'profile' && selectedRoomIds.length === 0
                    ? false
                    : getIsCategoryActive(category);
                  const isSelected = selectedCategory === category;
                  
                  return (
                    <div 
                      key={category} 
                      className={cn(classes.categoryBlock, {
                        [classes.categoryBlockSelected]: isSelected
                      })}
                    >
                      <div className={classes.categoryRow}>
                        <button
                          type="button"
                          className={classes.categoryIconButton}
                          onClick={() => handleCategorySelect(category)}
                        >
                          <MenuOutlined />
                        </button>
                        <span className={classes.categoryTitle}>
                          {t(NOTIFICATION_CATEGORY_LABEL_KEY_MAP[category])}
                        </span>
                        <Tooltip 
                          title={isDisabled ? t('notificationsSettings.selectRoomFirst') : ''}
                          placement="top"
                        >
                          <Switch
                            checked={isActive}
                            onChange={() => handleToggleCategory(category)}
                            size="small"
                            disabled={isDisabled}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        <div className={classes.footer}>
          <div className={classes.leftButtons}>
            <Button onClick={handleCriticalOnly} disabled={isLoading || isDisabled}>
              {t('notificationsSettings.criticalOnly')}
            </Button>
            <Button onClick={handleDefault} disabled={isLoading || isDisabled}>
              {t('notificationsSettings.default')}
            </Button>
          </div>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={isLoading || isDisabled || !isSettingsChanged}
            loading={saving}
          >
            {t('notificationsSettings.save')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={isConfirmCloseOpen}
        onCancel={() => setIsConfirmCloseOpen(false)}
        className={cn(classes.confirmModal, getTheme())}
        title={t('notificationsSettings.confirmClose.title')}
        centered
        footer={[
          <Button key='cancel' onClick={handleConfirmCancel}>
            {t('notificationsSettings.confirmClose.cancel')}
          </Button>,
          <Button key='save' type="primary" onClick={handleConfirmSave}>
            {t('notificationsSettings.confirmClose.save')}
          </Button>,
        ]}
      >
        {t('notificationsSettings.confirmClose.message')}
      </Modal>
    </>
  );
};

export default NotificationsSettingsModal;

